import React, { useState } from 'react';
import { Box, Alert, Typography } from '@mui/material';
import { useNotify } from 'react-admin';
import { AlertTriangle } from 'lucide-react';
import { MediaConfig } from '@musedrops/shared';
import { FormTextField } from './forms';
import { ImageField } from './forms/ImageField';
import { getGenPromptContent } from '../../data/gen-prompt';
import { LanguageApi } from '../../services/languageApi';

interface ImageGenerationSectionProps {
  // Entity identification
  entityType: 'show' | 'story';
  entityId?: string; // Optional because create mode doesn't have ID yet
  entitySlug: string;
  entityName: string; // show.name or story.title

  // Entity context for prompt population
  showTopic: string; // For both shows and stories
  languageKey?: string; // BCP 47 language code (e.g., 'en', 'he') for {language} placeholder

  // Image data
  imagePrompt: string | null;
  imageUpdatedAt?: string | null;

  // Change handlers
  // eslint-disable-next-line no-unused-vars
  onImagePromptChange: (value: string) => void;
  // eslint-disable-next-line no-unused-vars
  onImageUpdatedAtChange?: (timestamp: string) => void;

  // Generation controls
  onGenerate: () => Promise<void> | void;
  isGenerating: boolean;
  generationError: string | null;

  // Optional customization
  disabled?: boolean;
  allowStoryImages?: boolean; // For Show.allowStoryImages check (only relevant for stories)
}

export const ImageGenerationSection: React.FC<ImageGenerationSectionProps> = ({
  entityType,
  entityId,
  entitySlug,
  entityName,
  showTopic,
  languageKey,
  imagePrompt,
  imageUpdatedAt,
  onImagePromptChange,
  onImageUpdatedAtChange,
  onGenerate,
  isGenerating,
  generationError,
  disabled = false,
  allowStoryImages = true,
}) => {
  const imageFile =
    entityType === 'show'
      ? MediaConfig.show.coverImage
      : MediaConfig.story.coverImage;
  const notify = useNotify();
  const [copyError, setCopyError] = useState<string | null>(null);

  // Determine generate button tooltip based on state
  const getGenerateTitle = () => {
    if (!entityId) {
      return `Save ${entityType} first to generate image`;
    }
    if (!imagePrompt) {
      return 'Add an image prompt to generate image';
    }
    return 'Generate image using AI';
  };

  const handleCopyPrompt = async () => {
    if (!entityName || !showTopic || !imagePrompt) {
      setCopyError(
        `Missing required fields: ${entityType === 'show' ? 'show name' : 'story title'}, topic, or image prompt`
      );
      return;
    }

    setCopyError(null);

    try {
      // Get the appropriate prompt template based on entity type
      const promptTemplateKey =
        entityType === 'show' ? 'show_image' : 'story_image';
      const promptTemplate = await getGenPromptContent(promptTemplateKey);

      if (!promptTemplate) {
        setCopyError(`Failed to get ${entityType} image prompt template`);
        return;
      }

      // Replace template variables with actual values
      // Show uses: {{show-name}}, {{show-topic}}, {{show-image-prompt}}
      // Story uses: {{show-topic}}, {{story-title}}, {{story-image-prompt}}
      let populatedPrompt = promptTemplate;

      if (entityType === 'show') {
        populatedPrompt = populatedPrompt
          .replace(/\{\{show-name\}\}/g, entityName)
          .replace(/\{\{show-topic\}\}/g, showTopic)
          .replace(/\{\{show-image-prompt\}\}/g, imagePrompt);
      } else {
        populatedPrompt = populatedPrompt
          .replace(/\{\{show-topic\}\}/g, showTopic)
          .replace(/\{\{story-title\}\}/g, entityName)
          .replace(/\{\{story-image-prompt\}\}/g, imagePrompt);
      }

      // Replace {{language}} placeholder with language name
      if (languageKey) {
        try {
          const language = await LanguageApi.getByKey(languageKey);
          if (language?.name) {
            populatedPrompt = populatedPrompt.replace(
              /\{\{language\}\}/g,
              language.name
            );
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn(
            'Failed to fetch language name for key:',
            languageKey,
            err
          );
          // Continue without language replacement if fetch fails
        }
      }

      // Copy to clipboard
      await navigator.clipboard.writeText(populatedPrompt);

      notify('Prompt copied to clipboard', { type: 'success' });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to copy prompt';
      setCopyError(errorMessage);
    }
  };

  // Warning if this is a story and the show doesn't allow story images
  const showImageWarning = entityType === 'story' && allowStoryImages === false;

  return (
    <Box>
      {/* <Typography variant="h6" gutterBottom>
        {entityType === 'show' ? 'Show Image' : 'Story Image'}
      </Typography> */}

      {/* Warning for stories when show doesn't allow story images */}
      {showImageWarning && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AlertTriangle size={20} color="#ed6c02" />
          <Typography variant="body2">
            This show does not allow story images. All stories will use the show
            image.
          </Typography>
        </Box>
      )}

      {/* Only show image generation UI if images are allowed */}
      {!showImageWarning && (
        <>
          {/* Image Prompt Field */}
          <FormTextField
            label="Image Prompt"
            value={imagePrompt || ''}
            onChange={e => onImagePromptChange(e.target.value)}
            rows={2}
            sx={{ mb: 2 }}
            disabled={disabled}
            onCopy={handleCopyPrompt}
            copyTitle="Copy populated prompt to clipboard"
          />

          {/* Generation Error Alert */}
          {generationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {generationError}
            </Alert>
          )}

          {/* Copy Error Alert */}
          {copyError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {copyError}
            </Alert>
          )}

          {/* Image Upload and Preview - Uses conventional filename */}
          <ImageField
            label={`${entityType === 'show' ? 'Show' : 'Story'} Image`}
            value={imageUpdatedAt ? imageFile : null}
            onUpload={() => {}} // No-op: ImageField handles upload and calls onTimestampChanged
            onDelete={() => onImageUpdatedAtChange?.(null!)}
            onTimestampChanged={timestamp =>
              onImageUpdatedAtChange?.(timestamp)
            }
            onGenerate={onGenerate}
            entitySlug={entitySlug}
            entityId={entityId}
            storageFolder={entityType === 'show' ? 'shows' : 'stories'}
            disabled={disabled || !imagePrompt}
            imageUpdatedAt={imageUpdatedAt}
            showUpload
            showGenerate
            isGenerating={isGenerating}
            uploadTitle="Upload Image"
            generateTitle={getGenerateTitle()}
            helperText={
              !entityId
                ? `Save ${entityType} details first to enable image upload/generation`
                : undefined
            }
          />
        </>
      )}
    </Box>
  );
};
